import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  createCaptureDeviceApi,
  createWatchFolderMountApi,
  deleteCaptureDeviceApi,
  deleteWatchFolderMountApi,
  fetchCaptureDevicesApi,
  fetchScpInfoApi,
  fetchWatchFolderMountsApi,
  testCaptureDeviceApi,
  updateCaptureDeviceApi,
  updateWatchFolderMountApi,
} from '@services/api/dicom-capture.api'
import {
  ICaptureDevice,
  ICaptureDevicePayload,
  ICEchoTestResult,
  IScpInfo,
  IWatchFolderMount,
  IWatchFolderMountPayload,
} from '@dataTypes/dicom-capture.type'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'

const LOADING_CONTAINER = 'dicom-capture-settings'

export interface IUseDicomCapture {
  clinicId: string
  loading: boolean
  dataReady: boolean
  saving: boolean
  scpInfo: IScpInfo | null
  devices: ICaptureDevice[]
  watchMounts: IWatchFolderMount[]
  reload: () => Promise<void>
  saveDevice: (payload: ICaptureDevicePayload, editId?: string) => Promise<void>
  deleteDevice: (id: string) => Promise<void>
  toggleDevice: (device: ICaptureDevice) => Promise<void>
  testDevice: (id: string) => Promise<ICEchoTestResult | null>
  saveWatchMount: (
    payload: IWatchFolderMountPayload,
    editId?: string,
  ) => Promise<void>
  deleteWatchMount: (id: string) => Promise<void>
  toggleWatchMount: (mount: IWatchFolderMount) => Promise<void>
}

const useDicomCapture = (): IUseDicomCapture => {
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''

  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scpInfo, setScpInfo] = useState<IScpInfo | null>(null)
  const [devices, setDevices] = useState<ICaptureDevice[]>([])
  const [watchMounts, setWatchMounts] = useState<IWatchFolderMount[]>([])

  useSettingsSubpageLoading(!dataReady)

  const reload = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      setDevices([])
      setWatchMounts([])
      setScpInfo(null)
      setDataReady(true)
      return
    }
    setLoading(true)
    try {
      const [devicesRes, mountsRes] = await Promise.all([
        fetchCaptureDevicesApi(clinicId),
        fetchWatchFolderMountsApi(clinicId),
      ])
      setDevices(devicesRes.data?.capture_devices ?? [])
      setWatchMounts(mountsRes.data?.watch_folder_mounts ?? [])

      // SCP info is best-effort: the DIMSE listener may be disabled.
      try {
        const scpRes = await fetchScpInfoApi()
        setScpInfo(scpRes.data?.scp_info ?? null)
      } catch {
        setScpInfo(null)
      }
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to load DICOM capture settings'
      showToast(createFailedToast('DICOM Capture', message))
    } finally {
      setLoading(false)
      setDataReady(true)
    }
  }, [clinicId, showToast])

  useEffect(() => {
    void reload()
  }, [reload])

  const saveDevice = useCallback(
    async (payload: ICaptureDevicePayload, editId?: string): Promise<void> => {
      setSaving(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        if (editId) {
          await updateCaptureDeviceApi(editId, payload)
        } else {
          await createCaptureDeviceApi(payload)
        }
        showToast(
          createSuccessToast(
            'Saved',
            editId
              ? 'Capture device updated successfully.'
              : 'Capture device added successfully.',
          ),
        )
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to save capture device'
        showToast(createFailedToast('Capture device', message))
        throw e
      } finally {
        setSaving(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, reload, showLoading, showToast],
  )

  const deleteDevice = useCallback(
    async (id: string): Promise<void> => {
      setSaving(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        await deleteCaptureDeviceApi(id)
        showToast(
          createSuccessToast('Deleted', 'Capture device deleted successfully.'),
        )
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to delete capture device'
        showToast(createFailedToast('Capture device', message))
        throw e
      } finally {
        setSaving(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, reload, showLoading, showToast],
  )

  const toggleDevice = useCallback(
    async (device: ICaptureDevice): Promise<void> => {
      try {
        await updateCaptureDeviceApi(device.capture_device_id, {
          enabled: !device.enabled,
        })
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to update capture device'
        showToast(createFailedToast('Capture device', message))
      }
    },
    [reload, showToast],
  )

  const testDevice = useCallback(
    async (id: string): Promise<ICEchoTestResult | null> => {
      try {
        const res = await testCaptureDeviceApi(id)
        const result = res.data
        if (result?.seen) {
          showToast(
            createSuccessToast(
              'Connection OK',
              `Received a C-ECHO from ${
                result.calling_ae_title ?? 'the device'
              }.`,
            ),
          )
        } else {
          showToast(
            createFailedToast(
              'No recent C-ECHO',
              'No C-ECHO received in the last 60s. Ask the device to send one, then retry.',
            ),
          )
        }
        return result ?? null
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to run connection test'
        showToast(createFailedToast('Connection test', message))
        return null
      }
    },
    [showToast],
  )

  const saveWatchMount = useCallback(
    async (
      payload: IWatchFolderMountPayload,
      editId?: string,
    ): Promise<void> => {
      setSaving(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        if (editId) {
          await updateWatchFolderMountApi(editId, payload)
        } else {
          await createWatchFolderMountApi(payload)
        }
        showToast(
          createSuccessToast(
            'Saved',
            editId
              ? 'Watch folder updated successfully.'
              : 'Watch folder added successfully.',
          ),
        )
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to save watch folder'
        showToast(createFailedToast('Watch folder', message))
        throw e
      } finally {
        setSaving(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, reload, showLoading, showToast],
  )

  const deleteWatchMount = useCallback(
    async (id: string): Promise<void> => {
      setSaving(true)
      showLoading({ container: LOADING_CONTAINER })
      try {
        await deleteWatchFolderMountApi(id)
        showToast(
          createSuccessToast('Deleted', 'Watch folder deleted successfully.'),
        )
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to delete watch folder'
        showToast(createFailedToast('Watch folder', message))
        throw e
      } finally {
        setSaving(false)
        hideLoading(LOADING_CONTAINER)
      }
    },
    [hideLoading, reload, showLoading, showToast],
  )

  const toggleWatchMount = useCallback(
    async (mount: IWatchFolderMount): Promise<void> => {
      try {
        await updateWatchFolderMountApi(mount.watch_folder_mount_id, {
          enabled: !mount.enabled,
        })
        await reload()
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to update watch folder'
        showToast(createFailedToast('Watch folder', message))
      }
    },
    [reload, showToast],
  )

  return {
    clinicId,
    loading,
    dataReady,
    saving,
    scpInfo,
    devices,
    watchMounts,
    reload,
    saveDevice,
    deleteDevice,
    toggleDevice,
    testDevice,
    saveWatchMount,
    deleteWatchMount,
    toggleWatchMount,
  }
}

export default useDicomCapture
